import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import {
  Input,
  Form,
  Button,
} from '@lockerpm/design';

import authComponents from "./components";
import itemsComponents from "../../components/items";
import formsComponents from "../../components/forms";

import authServices from "../../services/auth";
import userServices from "../../services/user";

import common from "../../utils/common";
import global from "../../config/global";

const CreateMasterPassword = () => {
  const { t } = useTranslation();

  const { AuthCard, CreateMPConfirmModal, JoinEnterprise } = authComponents;
  const { UserInfo } = itemsComponents;
  const { GeneratePassword } = formsComponents;

  const locale = useSelector((state) => state.system.locale);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const enterpriseInvitations = useSelector((state) => state.enterprise.enterpriseInvitations);

  const [callingAPI, setCallingAPI] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [requestOrJoinOrg, setRequestOrJoinOrg] = useState(false);

  const [form] = Form.useForm();

  const masterPassword = Form.useWatch('masterPassword', form) || '';
  const masterPasswordHint = Form.useWatch('masterPasswordHint', form) || '';

  const handleCreateMasterPassword = async () => {
    form.validateFields().then(() => {
      setConfirmVisible(true)
    })
  }

  const enterpriseInvitation = useMemo(() => {
    return enterpriseInvitations[0]
  }, [enterpriseInvitations])

  const registerPayload = useMemo(() => {
    return {
      email: userInfo?.email,
      full_name: userInfo?.full_name,
      login_method: 'password',
      password: masterPassword,
      password_hint: masterPasswordHint,
      kdf: global.constants.CORE_JS_INFO.KDF,
      kdf_iterations: global.constants.CORE_JS_INFO.KDF_ITERATIONS,
      kdf_memory: null,
      kdf_parallelism: null
    }
  }, [
    userInfo,
    masterPassword,
    masterPasswordHint
  ])

  useEffect(() => {
    common.getTeams();
  }, []);

  const createMasterPassword = async () => {
    setCallingAPI(true);
    await userServices.register(registerPayload).then(async () => {
      if (enterpriseInvitation?.domain != null) {
        setRequestOrJoinOrg(true);
        setConfirmVisible(false);
      } else {
        await unlock()
      }
    }).catch((error) => {
      global.pushError(error)
    })
    setCallingAPI(false)
  }

  const unlock = async () => {
    setCallingAPI(true);
    await common.unlockToVault({
      ...registerPayload,
      username: userInfo?.email,
    }).then(() => {
      common.fetchUserInfo();
      common.getResourceCountries();
      common.getResourcePlans();
      common.setIsUnlocked();
    }).catch((error) => {
      const { code } = common.getErrorMessage(error);
      if (code == 1011) {
        global.navigate(global.keys.LOCK)
      } else {
        global.pushError(error)
      }
    });
    setCallingAPI(false);
  }

  return (
    <div
      className="lock-page auth-page"
    >
      <AuthCard
        className="w-[600px]"
        other={
          <div className="mt-4 text-center">
            <span>
              {t('setup_2fa.note')}
              <Button
                type="link"
                className="font-semibold"
                onClick={() => authServices.logout(true)}
              >
                {t('button.logout')}
              </Button>
            </span>
          </div>
        }
      >
        {
          requestOrJoinOrg ? <JoinEnterprise
            enterpriseInvitation={enterpriseInvitation}
            onUnlock={unlock}
          /> : <div className="w-full">
            <div className="w-full flex items-center justify-center">
              <p className="text-2xl font-semibold text-center">
                { t('create_master_password.title') }
              </p>
            </div>
            <p className="mb-6 mt-2">{t('create_master_password.description')}</p>
            <Form
              form={form}
              key={locale}
            >
              <div className="mt-4 mb-6 flex items-center justify-center">
                <UserInfo />
              </div>
              <Form.Item
                name="masterPassword"
                className='mb-0'
                rules={[
                  global.rules.REQUIRED(t('lock.master_password')),
                  global.rules.LATEST_LENGTH(t('lock.master_password'), 8),
                ]}
              >
                <Input.Password
                  placeholder={t('lock.master_password')}
                  size="large"
                  disabled={callingAPI}
                  onPressEnter={handleCreateMasterPassword}
                />
              </Form.Item>
              <GeneratePassword
                password={masterPassword}
                onFill={(v) => form.setFieldValue('masterPassword', v)}
              />
              <Form.Item
                name="confirmMasterPassword"
                className="mt-2"
                rules={[
                  global.rules.REQUIRED(t('create_master_password.confirm_master_password')),
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value && getFieldValue('masterPassword') !== value) {
                        return Promise.reject(new Error(t('validation.passwords_not_match')));
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder={t('create_master_password.confirm_master_password')}
                  size="large"
                  disabled={callingAPI}
                  onPressEnter={handleCreateMasterPassword}
                />
              </Form.Item>
              <Form.Item
                name="masterPasswordHint"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (value && getFieldValue('masterPassword') === value) {
                        return Promise.reject(new Error(t('validation.invalid', { name: t('create_master_password.master_password_hint') })));
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder={t('create_master_password.master_password_hint')}
                  size="large"
                  disabled={callingAPI}
                  onPressEnter={handleCreateMasterPassword}
                />
              </Form.Item>
              <Button
                className="w-full"
                size="large"
                type="primary"
                onClick={() => handleCreateMasterPassword()}
                disabled={callingAPI}
              >
                {t('create_master_password.title')}
              </Button>
            </Form>
            <p className="mb-6 mt-2">
              {t('create_master_password.note')}
            </p>
          </div>
        }
      </AuthCard>
      <CreateMPConfirmModal
        visible={confirmVisible}
        callingAPI={callingAPI}
        onClose={() => setConfirmVisible(false)}
        onConfirm={() => createMasterPassword()}
      />
    </div>
  );
}

export default CreateMasterPassword;